import React, { useEffect, useState } from 'react';
import { Select, TextInput, Button, Group, Stack, Text, Flex, Tooltip, Divider } from '@mantine/core';
import { useForm } from '@mantine/form';
import { Account } from './AccountCard';
import { createToast } from './Toast';
import { CalendarProvider, MessageKind } from '../utils/common';
import API from '../utils/const';
import { useTranslation } from 'react-i18next';
import { SyncRule } from "../utils/rule";
import { IconInfoCircle } from '@tabler/icons-react';
import { handleApiError } from '../utils/app';
import { SyncRuleFormValues } from './sync_rule/interface';
import {
    buildDestinationCalendarOptions,
    buildSourceCalendarOptions,
    calendarUUIDToProvider,
} from './sync_rule/calendarOptions';
import { getInitialSyncRuleFormValues } from './sync_rule/formDefaults';
import { getMappedText, applyBusyTemplate, applyTransparentTemplate } from './sync_rule/templates';
import { submitSyncRule } from './sync_rule/submitSyncRule';
import { SyncRuleAdvancedSettings } from './sync_rule/SyncRuleAdvancedSettings';
import { SyncRulePreview } from './sync_rule/SyncRulePreview';
import { trackSyncRuleCreated } from '../utils/analytics';

interface SyncRuleModalProps {
    close: () => void;
    rule: SyncRule | null;
    accounts: Account[];
    successCallback: () => void;
}

const SyncRuleModal: React.FC<SyncRuleModalProps> = ({ close, rule, accounts, successCallback }) => {
    const { t } = useTranslation(['app']);

    const [loading, setLoading] = useState(false);
    const [regexError, setRegexError] = useState<Boolean | null>(null);
    const defaultTitle = t('dashboard.sync.preview.example-title');
    const defaultDescription = t('dashboard.sync.preview.example-description');
    const [destinationTitle, setDestinationTitle] = useState(defaultTitle);
    const [destinationDescription, setDestinationDescription] = useState(defaultDescription);

    const defaultIsDestinationColourCompatible = (
        rule && rule.destination_uuid
        && calendarUUIDToProvider(accounts, rule.destination_uuid) === CalendarProvider.GOOGLE
    );
    const [isDestinationColourCompatible, setIsDestinationColourCompatible] = useState(defaultIsDestinationColourCompatible);

    const form = useForm<SyncRuleFormValues>({
        initialValues: getInitialSyncRuleFormValues(rule),
        validate: {
            source: (value) => (!value ? t('dashboard.required-field') : null),
            destination: (value, values) =>
                !value ? t('dashboard.required-field') :
                    value === values.source ? t('dashboard.sync.different-calendars') : null,
            summary: (value) => {
                if (!value) {
                    return t('dashboard.required-field');
                }
                const companyNamePlaceholder = t('dashboard.sync.template.transparent.company-name-placeholder');
                if (value.includes(companyNamePlaceholder)) {
                    return t('dashboard.sync.template.validation.replace-company-name', { placeholder: companyNamePlaceholder });
                }
                return null;
            },
            filterRuleField: (value, values) => {
                if ((values.filterRule || values.filterRuleType) && !value) {
                    return t('dashboard.required-field');
                }
                return null;
            },
            filterRuleType: (value, values) => {
                if ((values.filterRuleField || values.filterRule) && !value) {
                    return t('dashboard.required-field');
                }
                return null;
            },
            filterRule: (value, values) => {
                if ((values.filterRuleField || values.filterRuleType) && !value) {
                    return t('dashboard.required-field');
                }
                if (values.filterRuleType === 'regex') {
                    if (regexError) {
                        return <Text span size="xs">{t('dashboard.sync.invalid-regex')} <a href="https://regex101.com/" target="_blank">regex101.com</a></Text>;
                    }
                    return null;
                }
                return null;
            },
            bufferBefore: (value) => value < 0 ? t('dashboard.sync.buffer.cannot-be-negative') : null,
            bufferAfter: (value) => value < 0 ? t('dashboard.sync.buffer.cannot-be-negative') : null,
            pastEventsLookbackDays: (value, values) => {
                if (!values.syncPastEvents) {
                    return null;
                }
                if (value < 1 || value > 90) {
                    return t('dashboard.sync.past-events.lookback-range');
                }
                return null;
            },
            reminders: (value) => {
                if (!value) {
                    return null;
                }
                if (value.useDefault && value.overrides.length > 0) {
                    return t('dashboard.sync.reminders.cannot-have-both');
                }
                if (!value.useDefault && value.overrides.length === 0) {
                    return t('dashboard.sync.reminders.at-least-one-required');
                }
                return null;
            },
        },
        onValuesChange: (values) => {
            if (defaultIsDestinationColourCompatible) {
                return;
            }
            if (values.destination) {
                setIsDestinationColourCompatible(calendarUUIDToProvider(accounts, values.destination) === CalendarProvider.GOOGLE);
            }
        },
    });

    const handleSubmit = async (values: typeof form.values) => {
        setLoading(true);
        try {
            const response = await submitSyncRule(values, rule);
            if (!response.ok) {
                handleApiError(response, t, true);
                return;
            }
            if (!rule) {
                trackSyncRuleCreated('modal');
            }
            successCallback();
            close();
        } catch (error) {
            console.error('Error saving sync rule:', error);
            createToast(t('dashboard.sync.error.unexpected'), MessageKind.Error);
        } finally {
            setLoading(false);
            setRegexError(null);
        }
    };

    const sourceCalendars = accounts?.length
        ? buildSourceCalendarOptions(accounts)
        : rule
            ? [{ group: '', items: [{ value: rule.source_uuid, label: rule.source }] }]
            : [];
    const destinationCalendars = accounts?.length
        ? buildDestinationCalendarOptions(accounts, t)
        : rule
            ? [{ group: '', items: [{ value: rule.destination_uuid, label: rule.destination }] }]
            : [];

    const templateSetters = { setDestinationTitle, setDestinationDescription };

    let sourceAttributes = {};
    let destinationAttributes = {};
    if (rule) {
        sourceAttributes = { defaultValue: rule.source_uuid, disabled: true };
        destinationAttributes = { defaultValue: rule.destination_uuid, disabled: true };
    }

    useEffect(() => {
        if (regexError != null) {
            form.onSubmit(handleSubmit)();
        }
    }, [regexError]);

    const onSubmitHook = async () => {
        setRegexError(null);
        if (form.values.filterRuleType === 'regex') {
            try {
                const response = await fetch(`${API}/validate-regex`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ regex: form.values.filterRule }),
                });
                if (response.status === 200) {
                    setRegexError(false);
                } else if (response.status === 400) {
                    setRegexError(true);
                } else {
                    createToast(t('dashboard.sync.filter.error-validating'), MessageKind.Error);
                }
            } catch (error) {
                handleApiError(error, MessageKind.Error, true);
            }
        } else {
            setRegexError(false);
        }
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmitHook(); }}>
            <Stack gap="sm">
                <Text size="lg" c="dimmed">{t('dashboard.sync.select-calendars')}</Text>
                <Select
                    placeholder={t("dashboard.sync.draft.source")}
                    data={sourceCalendars}
                    {...sourceAttributes}
                    {...form.getInputProps('source')}
                    searchable
                    required
                />
                <Select
                    placeholder={t("dashboard.sync.draft.destination")}
                    data={destinationCalendars}
                    {...destinationAttributes}
                    {...form.getInputProps('destination')}
                    searchable
                    required
                />
                <Divider my="sm" />
                <Select
                    label={t('dashboard.sync.template.label')}
                    placeholder={t('dashboard.sync.template.placeholder')}
                    data={[
                        { value: 'private', label: t('dashboard.sync.template.private.label') },
                        { value: 'transparent', label: t('dashboard.sync.template.transparent.label') },
                    ]}
                    clearable
                    onChange={(value) => {
                        if (value === 'private') {
                            applyBusyTemplate(form, templateSetters, t('dashboard.sync.template.private.busy-text'));
                        } else if (value === 'transparent') {
                            applyTransparentTemplate(
                                form,
                                templateSetters,
                                defaultTitle,
                                defaultDescription,
                                t('dashboard.sync.template.transparent.company-name-placeholder'),
                            );
                        }
                    }}
                    renderOption={({ option }) => {
                        const isPrivate = option.value === 'private';
                        return (
                            <Stack gap={2}>
                                <Text fw={500} size="sm">
                                    {isPrivate
                                        ? t('dashboard.sync.template.private.label')
                                        : t('dashboard.sync.template.transparent.label')}
                                </Text>
                                <Text size="sm" c="dimmed">
                                    {isPrivate
                                        ? t('dashboard.sync.template.private.description')
                                        : t('dashboard.sync.template.transparent.description')}
                                </Text>
                            </Stack>
                        );
                    }}
                />
                <Divider my="sm" />
                <Text size="lg" c="dimmed">{t('dashboard.sync.map-title-description')}</Text>
                <TextInput
                    label={
                        <Flex gap={0} justify="flex-start" align="center" mb="0%">
                            <Text size="sm">{t('dashboard.sync.label-custom-title')}</Text>
                            <Text size="sm" c="red" pl="2px" pr="xs">*</Text>
                            <Tooltip label={t('dashboard.sync.title-tooltip')} multiline w={500}>
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </Flex>
                    }
                    placeholder={t('dashboard.sync.placeholder-title')}
                    {...form.getInputProps('summary')}
                    onChange={(e) => {
                        if (e.currentTarget.value === '') {
                            setDestinationTitle(t('dashboard.sync.preview.error-empty'));
                        } else {
                            setDestinationTitle(getMappedText(defaultTitle, e.currentTarget.value));
                        }
                        form.getInputProps('summary').onChange?.(e);
                    }}
                />
                <TextInput
                    label={
                        <Flex gap="xs" justify="flex-start" align="center" mb="0%">
                            <Text size="sm">{t('dashboard.sync.label-custom-description')}</Text>
                            <Tooltip label={t('dashboard.sync.description-tooltip')} multiline w={500}>
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </Flex>
                    }
                    placeholder={t('dashboard.sync.placeholder-description')}
                    {...form.getInputProps('description')}
                    onChange={(e) => {
                        setDestinationDescription(getMappedText(defaultDescription, e.currentTarget.value));
                        form.getInputProps('description').onChange?.(e);
                    }}
                />
                <SyncRulePreview
                    sourceTitle={defaultTitle}
                    sourceDescription={defaultDescription}
                    destinationTitle={destinationTitle}
                    destinationDescription={destinationDescription}
                    titleLabel={t('dashboard.sync.preview.title')}
                    sourceLabel={t('dashboard.sync.preview.source')}
                    destinationLabel={t('dashboard.sync.preview.destination')}
                />
                <Divider my="sm" />
                <Text size="lg" c="dimmed">{t('dashboard.sync.advanced-settings')}</Text>
                <SyncRuleAdvancedSettings
                    form={form}
                    isDestinationColourCompatible={!!isDestinationColourCompatible}
                />
                <Group justify="flex-end" mt="md">
                    <Button variant="outline" onClick={close}>{t("common.cancel")}</Button>
                    <Button type="submit" loading={loading}>{t("common.save")}</Button>
                </Group>
            </Stack>
        </form>
    );
};

export default SyncRuleModal;
